import { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Card, CardContent, Typography } from '@mui/material';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

const StyledCardContent = styled(CardContent)({
  padding: '1rem',
  paddingBottom: '1rem !important',
});

/**
 * Componente visual tipo "toast" o "sugerencia", con soporte para icono, color, animación de aparición/desaparición,
 * y personalización del fondo y texto. Puede desvanecerse automáticamente luego de un tiempo configurable.
 *
 * * @component
 *  * @param {Object} props - Props del componente.
 *  * @param {React.ReactNode} props.children - Contenido textual o JSX que se mostrará dentro del prompt.
 *  * @param {React.CSSProperties} [props.textStyle] - Estilo en línea adicional aplicado al texto.
 *  * @param {string} [props.textClasses] - Clases CSS adicionales para el texto.
 *  * @param {string} props.textColor - Color del texto (por ejemplo, `#FFFFFF` o `rgb(0,0,0)`).
 *  * @param {string} [props.backgroundColor='#e2f3fb'] - Color de fondo del prompt (puede ser hex, rgb o rgba).
 *  * @param {React.ReactNode} [props.icon] - Icono o contenido gráfico que se mostrará a la izquierda del texto.
 *  * @param {string} [props.className] - Clases CSS adicionales para el componente Card.
 *  * @param {number} [props.duration=3000] - Duración en milisegundos antes de que el prompt se desvanezca (solo si `enableFade` es `true`).
 *  * @param {boolean} [props.enableFade=false] - Si es `true`, el componente aplicará un efecto de fade-out automático después de `duration` ms.
 *  * @returns {JSX.Element} El componente `SuggestionPrompt` renderizado.
 */
export const SuggestionPrompt = ({
  children,
  textStyle,
  textClasses,
  textColor,
  backgroundColor = '#e2f3fb',
  icon,
  className,
  duration = 3000,
  enableFade = false,
}) => {
  const [visible, setVisible] = useState(true);
  const [contentKey, setContentKey] = useState('');
  const timeFaceRef = useRef(null);

  useEffect(() => {
    const newKey = typeof children === 'string' ? children : JSON.stringify(children);
    setContentKey(newKey);
    setVisible(true);

    if (!enableFade) return;
    timeFaceRef.current = setTimeout(() => {
      setVisible(false);
    }, duration);
  }, [children, duration, enableFade]);

  useEffect(() => {
    return () => {
      clearTimeout(timeFaceRef.current);
    };
  }, []);

  const renderContent = () => (
    <Card sx={{ backgroundColor }} className={clsx('flex flex-row shadow-none', className)}>
      <StyledCardContent className="shadow-none flex items-center gap-16">
        {icon && <div className="flex flex-col">{icon}</div>}
        <Typography
          color={textColor}
          style={textStyle}
          className={clsx(textClasses, 'text-sm p-0 m-0')}
        >
          {children}
        </Typography>
      </StyledCardContent>
    </Card>
  );

  if (!enableFade) {
    return renderContent();
  }

  return (
    <AnimatePresence mode="wait">
      {visible && (
        <motion.div
          className={clsx('flex w-full flex-row')}
          key={contentKey}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {renderContent()}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
